import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { moduleClientFactory } from '../../../../api-service/module-client-factory';
import {
  NudgeQuestionnaireModel,
  NudgeQuestionnaireOfferModel,
  PseudoFasitEntityDiscriminatorEnum,
  QuestionnaireNudgeOutcome,
} from '../../../../api-service/clients';
import { Button } from '../../../../components/button';
import { SingleSelect } from '../../../../components/single-select';
import { INudgeCitizen } from './nudge-citizen-list';
import { useNudgeNotifier } from './use-nudge-notifier';

const client = moduleClientFactory.createForumCaseworkerClient();

const outcomeTexts: Record<QuestionnaireNudgeOutcome, string> = {
  [QuestionnaireNudgeOutcome.Sent]: 'Sendt',
  [QuestionnaireNudgeOutcome.SentTooRecently]: 'Ikke sendt - har fået spørgeskemaet for nylig',
  [QuestionnaireNudgeOutcome.AlreadyAnsweredAndOpen]: 'Ikke sendt - har et besvaret spørgeskema der ikke er lukket',
};

const contextTexts: Record<PseudoFasitEntityDiscriminatorEnum, string> = {
  [PseudoFasitEntityDiscriminatorEnum.None]: 'Ingen',
  [PseudoFasitEntityDiscriminatorEnum.Offer]: 'Tilbud',
  [PseudoFasitEntityDiscriminatorEnum.ConsentDeclaration]: 'Samtykkeerklæring',
  [PseudoFasitEntityDiscriminatorEnum.Conversation]: 'Samtale',
  [PseudoFasitEntityDiscriminatorEnum.Progression]: 'Progression',
};

function contextOf(questionnaire?: NudgeQuestionnaireModel) {
  return questionnaire?.pseudoFasitEntityDiscriminator ?? PseudoFasitEntityDiscriminatorEnum.None;
}

function questionnaireLabel(questionnaire: NudgeQuestionnaireModel) {
  const context = contextOf(questionnaire);
  return context === PseudoFasitEntityDiscriminatorEnum.None ? questionnaire.name ?? '' : `${questionnaire.name} (${contextTexts[context]})`;
}

export interface INudgeWithQuestionnaireProps {
  citizens: INudgeCitizen[];
}

export function NudgeWithQuestionnaire(props: INudgeWithQuestionnaireProps) {
  const { citizens } = props;
  const [questionnaires, setQuestionnaires] = useState<NudgeQuestionnaireModel[]>([]);
  const [questionnaireId, setQuestionnaireId] = useState('');
  const [offers, setOffers] = useState<NudgeQuestionnaireOfferModel[]>();
  const [offerId, setOfferId] = useState('');
  const [isSending, setIsSending] = useState(false);
  const { notify, notifyLoadError } = useNudgeNotifier();

  const questionnaire = questionnaires.find(candidate => candidate.id === questionnaireId);
  const isAboutAnOffer = contextOf(questionnaire) === PseudoFasitEntityDiscriminatorEnum.Offer;

  useEffect(() => {
    client
      .getNudgeQuestionnaires()
      .then(result => setQuestionnaires(result.questionnaires ?? []))
      .catch(error => {
        setQuestionnaires([]);
        notifyLoadError(error, 'Spørgeskemaerne kunne ikke hentes.');
      });
  }, []);

  async function chooseQuestionnaire(chosen: NudgeQuestionnaireModel) {
    setQuestionnaireId(chosen.id);
    setOfferId('');
    setOffers(undefined);
    if (contextOf(chosen) !== PseudoFasitEntityDiscriminatorEnum.Offer) {
      return;
    }
    try {
      const result = await client.getNudgeQuestionnaireOffers({ questionnaireId: chosen.id });
      setOffers(result.offers ?? []);
    } catch (error) {
      setOffers([]);
      notifyLoadError(error, 'Tilbuddene kunne ikke hentes.');
    }
  }

  async function send() {
    setIsSending(true);
    try {
      const result = await client.nudgeCitizensWithQuestionnaire({
        citizenIds: citizens.map(citizen => citizen.citizenId),
        questionnaireId,
        pseudoFasitEntityDiscriminator: contextOf(questionnaire),
        ...(isAboutAnOffer && { fasitEntityId: offerId }),
      });
      const results = result.results ?? [];
      const sentCount = results.filter(nudge => nudge.outcome === QuestionnaireNudgeOutcome.Sent).length;
      const skipped = results.filter(nudge => nudge.outcome !== QuestionnaireNudgeOutcome.Sent);
      const parts = [
        `Spørgeskemaet er sendt til ${sentCount} ${sentCount === 1 ? 'borger' : 'borgere'}`,
        ...skipped.map(nudge => `${nameOf(nudge.citizenId)}: ${outcomeTexts[nudge.outcome]}`),
      ];
      notify(`${parts.join('. ')}.`, skipped.length > 0 ? 'info' : 'success');
    } catch {
      notify('Spørgeskemaet blev ikke sendt.', 'error');
    } finally {
      setIsSending(false);
    }
  }

  function nameOf(citizenId: string) {
    return citizens.find(citizen => citizen.citizenId === citizenId)?.name || citizenId;
  }

  const canSend = !!questionnaireId && citizens.length > 0 && (!isAboutAnOffer || !!offerId);

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <SingleSelect<NudgeQuestionnaireModel>
        label="Spørgeskema"
        value={questionnaire}
        options={questionnaires}
        getOptionLabel={questionnaireLabel}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderOption={(optionProps, option) => (
          <li {...optionProps} key={option.id}>
            {questionnaireLabel(option)}
          </li>
        )}
        onChange={chooseQuestionnaire}
        disableClearable
      />
      {isAboutAnOffer && offers?.length === 0 && <Typography>Der er ingen tilbud, som spørgeskemaet kan sendes om.</Typography>}
      {isAboutAnOffer && !!offers?.length && (
        <SingleSelect<NudgeQuestionnaireOfferModel>
          label="Tilbud"
          value={offers.find(offer => offer.id === offerId)}
          options={offers}
          getOptionLabel={offer => offer.name ?? ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderOption={(optionProps, offer) => (
            <li {...optionProps} key={offer.id}>
              {offer.name}
            </li>
          )}
          onChange={offer => setOfferId(offer.id)}
          disableClearable
        />
      )}
      <Box display="flex" justifyContent="flex-end">
        <Button color="primary" onClick={send} disabled={!canSend} loading={isSending}>
          Send spørgeskema
        </Button>
      </Box>
    </Box>
  );
}
